/*
 * Optimizely Feature Experimentation — shared shopper attribute helpers.
 *
 * Pure and SDK-free so both the /optimizely-page SSR targeting session and the
 * client-side conversion tracking build identical user contexts; decisions and
 * events must share userId + attributes or Optimizely splits the visitor.
 */

/**
 * Normalize customer group IDs (CSV string or array) into a trimmed,
 * de-duplicated array. Group IDs are case-sensitive, so casing is preserved.
 * @param {string|string[]|*} groups
 * @returns {string[]}
 */
function normalizeCustomerGroups(groups) {
    let list = []
    if (Array.isArray(groups)) list = groups
    else if (typeof groups === 'string') list = groups.split(',')
    const ids = list.map((id) => String(id).trim()).filter(Boolean)
    return [...new Set(ids)]
}

/* Visitor-level keys the browser (or the CDN, for country) supplies. FX has no
 * built-in attributes, so these are sent explicitly on decisions and events. */
const VISITOR_CONTEXT_KEYS = [
    'device',
    'browser',
    'language',
    'locale',
    'referrer',
    'source',
    'campaign',
    'returning_user',
    'country'
]
const MAX_STRING = 200

/**
 * Loyalty tier name from the shopper profile (`c_loyaltyMostRecentTier`).
 * Optimizely audiences match this string. Empty when the profile has no tier.
 * @param {string|string[]|*} tier
 * @returns {string}
 */
function normalizeLoyaltyTier(tier) {
    const raw = Array.isArray(tier) ? tier[0] : tier
    if (typeof raw !== 'string') return ''
    return raw.trim().slice(0, MAX_STRING)
}

/**
 * Whitelist + scalar-only copy of a visitor context (client-sent header or
 * local capture). Unknown keys, objects and empty strings are dropped.
 * @param {Object} [input]
 * @returns {Object}
 */
function sanitizeVisitorContext(input) {
    if (!input || typeof input !== 'object') return {}
    const out = {}
    for (const key of VISITOR_CONTEXT_KEYS) {
        const value = input[key]
        if (typeof value === 'boolean') out[key] = value
        else if (typeof value === 'string' && value.trim())
            out[key] = value.trim().slice(0, MAX_STRING)
    }
    return out
}

/** Storefront country from a locale id: en-CA / fr-CA → CA, en-US → US. */
function siteCountryFromLocale(localeId) {
    return (
        String(localeId || '')
            .split('-')[1]
            ?.toUpperCase() || ''
    )
}

/**
 * Build the scalar attribute map sent to Optimizely's user context. Names
 * follow the Web Experimentation project (site_* = storefront, is_logged_in)
 * so audiences read the same across both platforms. Customer groups are
 * exposed as a CSV string AND a per-group boolean `group_<ID>: true`.
 * `loyalty_tier` is the logged-in shopper's profile tier name. Guests and
 * members without a tier omit it. pathname / page_type / optly_qa are applied
 * after the visitor whitelist so a stale or spoofed visitor blob cannot set
 * them. They travel as query params.
 * @param {{brandSlug: string, localeId: string, isLoggedIn: boolean, customerGroups?: string[], loyaltyTier?: string, visitorContext?: Object, page?: {pathname?: string, page_type?: string}, optlyQa?: boolean}} params
 * @returns {Object} scalar-only attribute map
 */
function buildOptimizelyAttributes({
    brandSlug,
    localeId,
    isLoggedIn,
    customerGroups = [],
    loyaltyTier = '',
    visitorContext,
    page,
    optlyQa = false
}) {
    const attributes = {
        ...sanitizeVisitorContext(visitorContext),
        brand: brandSlug,
        site_locale: localeId,
        site_country: siteCountryFromLocale(localeId),
        site_language: String(localeId || '')
            .split('-')[0]
            .toLowerCase(),
        is_logged_in: !!isLoggedIn,
        customerGroups: customerGroups.join(','),
        optly_qa: optlyQa === true
    }
    const pathname =
        typeof page?.pathname === 'string' ? page.pathname.trim().slice(0, MAX_STRING) : ''
    const pageType =
        typeof page?.page_type === 'string' ? page.page_type.trim().slice(0, MAX_STRING) : ''
    if (pathname) attributes.pathname = pathname
    if (pageType) attributes.page_type = pageType
    customerGroups.forEach((groupId) => {
        attributes[`group_${groupId}`] = true
    })
    const tier = isLoggedIn ? normalizeLoyaltyTier(loyaltyTier) : ''
    if (tier) attributes.loyalty_tier = tier
    return attributes
}

/**
 * Query string shared by /cms-optimizely-decisions, /feature-flag-decisions and
 * /feature-flag-impression. brand and locale stay first, matching the
 * previous decisions URL.
 * @param {{brand?: string, locale?: string, pathname?: string, pageType?: string, optlyQa?: boolean}} params
 * @returns {URLSearchParams}
 */
function buildDecisionSearchParams({brand, locale, pathname, pageType, optlyQa}) {
    return new URLSearchParams({
        brand: brand || '',
        locale: locale || '',
        pathname: pathname || '/',
        page_type: pageType || '',
        optly_qa: optlyQa === true ? 'true' : 'false'
    })
}

const queryText = (value) => (typeof value === 'string' ? value.trim().slice(0, MAX_STRING) : '')

/**
 * Page and QA attributes from a decisions request query. Strings inside
 * x-visitor-context are ignored; only this query is trusted for these keys.
 * @param {Object} [query]
 * @returns {{page: {pathname?: string, page_type?: string}, optlyQa: boolean}}
 */
function readDecisionQuery(query) {
    const pathname = queryText(query?.pathname)
    const pageType = queryText(query?.page_type)
    const page = {}
    if (pathname) page.pathname = pathname
    if (pageType) page.page_type = pageType
    return {page, optlyQa: query?.optly_qa === 'true'}
}

/**
 * Optimizely attributes and event tags accept only scalar values; arrays and
 * objects are dropped so they never reach the SDK.
 * @param {Object} [values]
 * @returns {Object} entries containing only string/number/boolean/null values
 */
function pruneScalars(values) {
    if (!values || typeof values !== 'object') return {}
    return Object.entries(values).reduce((acc, [key, value]) => {
        if (
            value === null ||
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
        ) {
            acc[key] = value
        }
        return acc
    }, {})
}

export {
    normalizeCustomerGroups,
    normalizeLoyaltyTier,
    buildOptimizelyAttributes,
    buildDecisionSearchParams,
    readDecisionQuery,
    pruneScalars,
    sanitizeVisitorContext,
    siteCountryFromLocale,
    VISITOR_CONTEXT_KEYS
}
